import { useState, useCallback, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Zap, Star, Sparkles } from 'lucide-react';
import { Button, Checkbox, EmptyState } from '@/components/v2/ui';
import { upscaleImageUpload, type UpscaleParams } from '@/lib/api/imageOperations';
import { type ImageOptimizeState } from './useImageOptimizeState';
import { cn } from '@/lib/utils';

interface Props {
  state: ImageOptimizeState;
  setState: Dispatch<SetStateAction<ImageOptimizeState>>;
}

type Model = NonNullable<UpscaleParams['model']>;

interface ModelInfo {
  value: Model;
  label: string;
  speed: number;
  quality: number;
  descKey: string;
}

const MODELS: ModelInfo[] = [
  { value: 'espcn', label: 'ESPCN', speed: 5, quality: 3, descKey: 'imageOptimize.quality.espcnDesc' },
  { value: 'fsrcnn', label: 'FSRCNN', speed: 5, quality: 3, descKey: 'imageOptimize.quality.fsrcnnDesc' },
  { value: 'lapsrn', label: 'LapSRN', speed: 3, quality: 4, descKey: 'imageOptimize.quality.lapsrnDesc' },
  { value: 'edsr', label: 'EDSR', speed: 1, quality: 5, descKey: 'imageOptimize.quality.edsrDesc' },
  { value: 'lanczos', label: 'Lanczos', speed: 5, quality: 2, descKey: 'imageOptimize.quality.lanczosDesc' },
];

function Rating({ value, icon: Icon, color }: { value: number; icon: React.ElementType; color: string }) {
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Icon key={i} className={cn('h-2.5 w-2.5', i < value ? color : 'text-neutral-300 dark:text-neutral-600')} />
      ))}
    </div>
  );
}

export function QualityGroup({ state, setState }: Props) {
  const { t } = useTranslation();
  const [model, setModel] = useState<Model>('espcn');
  const [scaleFactor, setScaleFactor] = useState(2.0);
  const [applySharpening, setApplySharpening] = useState(true);

  const handleUpscale = useCallback(async () => {
    if (!state.currentFile) return;
    setState(prev => ({ ...prev, isEditing: true }));
    try {
      const result = await upscaleImageUpload(state.currentFile, {
        scale_factor: scaleFactor,
        model,
        apply_sharpening: applySharpening,
      });
      if (!result.success) throw new Error(result.error || 'Failed');
      setState(prev => ({
        ...prev,
        isEditing: false,
        editedImageUrl: result.image_url,
        editedS3Key: result.image_id ?? prev.editedS3Key,
        editHistory: [...prev.editHistory, {
          timestamp: new Date(),
          action: 'upscale',
          labelKey: 'imageOptimize.quality.historyUpscale',
          imageUrl: result.image_url,
          s3Key: result.image_id ?? '',
        }],
      }));
    } catch {
      setState(prev => ({ ...prev, isEditing: false }));
    }
  }, [state.currentFile, scaleFactor, model, applySharpening, setState]);

  return (
    <div className="space-y-3">
      {/* Model Selection */}
      <div>
        <p className="text-sm font-medium mb-2">{t('imageOptimize.quality.aiModel')}</p>
        <div className="space-y-1.5">
          {MODELS.map(m => (
            <button
              key={m.value}
              onClick={() => setModel(m.value)}
              disabled={state.isEditing}
              className={cn(
                'w-full p-2.5 rounded-lg border text-left transition-colors',
                model === m.value
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                  : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300'
              )}
            >
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-sm font-medium">{m.label}</span>
                {model === m.value && <Check className="h-3.5 w-3.5 text-green-500" />}
              </div>
              <p className="text-2xs text-neutral-500 mb-1.5">{t(m.descKey)}</p>
              <div className="flex items-center gap-3 text-2xs">
                <div className="flex items-center gap-1">
                  <span className="text-neutral-400">Speed:</span>
                  <Rating value={m.speed} icon={Zap} color="text-amber-500" />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-neutral-400">Quality:</span>
                  <Rating value={m.quality} icon={Star} color="text-green-500" />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Scale Factor */}
      <div className="space-y-1.5">
        <div className="flex justify-between items-center">
          <label className="text-sm font-medium">{t('imageOptimize.quality.scaleFactor')}</label>
          <span className="text-sm font-bold text-green-600 dark:text-green-400">{scaleFactor}x</span>
        </div>
        <input
          type="range" min="1.5" max="2.0" step="0.1" value={scaleFactor}
          onChange={e => setScaleFactor(Number(e.target.value))}
          disabled={state.isEditing}
          className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-neutral-200 dark:bg-neutral-700 accent-green-500"
        />
        <div className="flex justify-between text-2xs text-neutral-500">
          <span>1.5x</span>
          <span>2.0x</span>
        </div>
      </div>

      {/* Sharpening Toggle */}
      <div className="flex items-center justify-between p-2.5 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div>
          <p className="text-sm font-medium">{t('imageOptimize.quality.sharpen')}</p>
          <p className="text-xs text-neutral-500">{t('imageOptimize.quality.sharpenHint')}</p>
        </div>
        <Checkbox
          checked={applySharpening}
          onChange={e => setApplySharpening(e.target.checked)}
          disabled={state.isEditing}
        />
      </div>

      {/* Upscale Button */}
      <Button variant="success" className="w-full" disabled={!state.currentFile || state.isEditing}
        loading={state.isEditing} onClick={handleUpscale}>
        {t('imageOptimize.quality.applyUpscale', { factor: scaleFactor })}
      </Button>

      {/* Detail Enhance Teaser */}
      <div className="border-t border-neutral-200 dark:border-neutral-700 pt-3">
        <EmptyState
          size="sm"
          icon={<Sparkles className="h-8 w-8" />}
          title={t('imageOptimize.quality.detailEnhance')}
          description={t('imageOptimize.quality.comingSoon')}
        />
      </div>
    </div>
  );
}
